import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import { withCurrentTemplate } from '@/lib/withCurrentTemplate.ts';
import { ManualMeetingDialogs } from './ManualMeetingDialogs.tsx';
import { MeetingDialogs } from './MeetingDialogs.tsx';
import { MeetingDocuments } from './MeetingDocuments.tsx';
import { MeetingHeader } from './MeetingHeader.tsx';
import { MeetingRecordingDialogs } from './MeetingRecordingDialogs.tsx';
import { MeetingSettingsRow } from './MeetingSettingsRow.tsx';
import { NotetakerIssueBanner } from './NotetakerIssueBanner.tsx';
import { OfflineRecordingBanner } from './OfflineRecordingBanner.tsx';
import { meetingAttendeeList } from './meetingAttendeeList.ts';
import type {
  MeetingDialog,
  MeetingDialogKind,
  MeetingTemplatePick,
} from './meetingDialog.ts';
import type { MeetingPanel } from './meetingPanel.ts';
import { useMeetingPage } from './useMeetingPage.ts';
import { useMeetingRecording } from './useMeetingRecording.ts';
import { useMeetingWrites } from './useMeetingWrites.ts';
import { useReportOpenedDocument } from './useReportOpenedDocument.ts';
import type { OfflineRecordingState } from '@/lib/useOfflineRecording.ts';
import { wundoScope } from '@/lib/addToWundo.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import {
  correctMeeting,
  setMeetingTemplate,
  type MeetingTemplateChoice,
} from '@/lib/meetingPage.ts';
import { fetchMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import { buildRichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingViewProps {
  meetingId: string;
  /* Which document is open; the page holds it, so the chat beside it can follow. */
  panel: MeetingPanel;
  onPanelChange: (panel: MeetingPanel) => void;
  /* The meeting was deleted: whoever put this view on screen takes it off. */
  onLeave?: () => void;
}

/* After a gesprekstype pick: put the next question on screen, or save it. */
type TemplateStep =
  | { kind: 'ask'; pick: MeetingTemplatePick }
  | { kind: 'save'; choice: MeetingTemplateChoice };

/**
 * One meeting, whole: the bar that names it, the row of what it is set to, and
 * the two documents under them. The chat stands beside it, so the page folds
 * for the width of its own container (`@container`) rather than the window.
 * A meeting Wundo stays out of gets the same view, with the offer to add it.
 */
export function MeetingView({
  meetingId,
  panel,
  onPanelChange: setPanel,
  onLeave,
}: MeetingViewProps) {
  const { user, pageQuery, refresh } = useMeetingPage(meetingId);
  const { data: page, isError } = pageQuery;
  const userId = user?.id ?? '';
  useReportOpenedDocument(user?.id, meetingId, panel, page);

  const { dialog, setDialog, closeDialog } = useMeetingDialog();
  const [typesWanted, setTypesWanted] = useState(false);
  const templates = useMeetingTemplates(user?.id, typesWanted || dialog?.kind === 'edit');

  /* Only a superadmin can sign in as somebody; the server refuses others anyway. */
  const impersonatedBy = useImpersonatedBy();
  const staff = user?.role === 'superadmin' || Boolean(impersonatedBy);

  const editor = useEditorWiring(userId, page, refresh);

  const writes = useMeetingWrites({ userId, meetingId, closeDialog });
  useFirstPrepGeneration(page, panel, writes.generatePrep);
  /* Held here: the pill, the dialogs and the verslag tab share one recording. */
  const recording = useMeetingRecording({
    userId,
    meetingId,
    offline: page?.offline_recording,
    onChanged: refresh,
    onStopped: () => setPanel('report'),
  });

  /* A failed refetch keeps the page that is on screen; only a first load has nothing to show. */
  if (!page && isError) {
    return <p className='text-base text-danger'>{m.meeting_load_failed()}</p>;
  }
  if (!page || !user || !editor) {
    return <p className='text-base text-ink-muted'>{m.meeting_loading()}</p>;
  }

  const saveTemplate = (choice: MeetingTemplateChoice) =>
    writes.run(() => setMeetingTemplate(user.id, meetingId, choice));

  const advanceTemplate = (pick: MeetingTemplatePick) => {
    const step = nextTemplateStep(page, pick);
    if (step.kind === 'save') saveTemplate(step.choice);
    else setDialog(step.pick);
  };

  /* As the agenda's "+ Wundo": no question, the widest reach the meeting allows. */
  const addToWundo = () =>
    writes.run(() =>
      correctMeeting(user.id, meetingId, {
        follow: true,
        scope: wundoScope(page.decision.never_automatic),
      }),
    );

  const shareNotes = (content: string) => {
    closeDialog(['share-notes']);
    setPanel('report');
    writes.pasteGenerate.mutate(content);
  };

  const attendees = meetingAttendeeList(page.meeting);

  return (
    <div className='@container'>
      <MeetingHeader
        page={page}
        attendees={attendees}
        adding={writes.correction.isPending}
        onOpenDialog={setDialog}
        onAddToWundo={addToWundo}
      />

      <MeetingSettingsRow
        page={page}
        templates={templates}
        calendarEnabled={user.calendar_enabled}
        staff={staff}
        correction={writes.correction}
        uploadDocument={writes.uploadDocument}
        recordingOffer={recording.offer}
        canRecord={recording.recorder.supported}
        recordingPending={recording.pending}
        runningRecording={recording.runningRecording}
        onStartRecording={recording.askToRecord}
        onOpenRecording={recording.openLive}
        onStopRecording={recording.stop}
        onResumeRecording={recording.askToResume}
        onOpenTypes={() => setTypesWanted(true)}
        onPickTemplate={(template) =>
          advanceTemplate({ kind: 'template', template, scope: null })
        }
        onOpenDialog={setDialog}
      />

      {offersRecordingHere(page, recording.recorder.state) && (
        <OfflineRecordingBanner
          supported={recording.recorder.supported}
          pending={recording.pending}
          onStart={recording.askToRecord}
        />
      )}

      {page.notetaker_issue && (
        <NotetakerIssueBanner
          issue={page.notetaker_issue}
          meeting={page.meeting}
          onShareNotes={() => setDialog({ kind: 'share-notes' })}
        />
      )}

      <MeetingDocuments
        page={page}
        userId={user.id}
        meetingId={meetingId}
        panel={panel}
        onPanelChange={setPanel}
        editor={editor}
        generation={writes.generation}
        pasteGenerate={writes.pasteGenerate}
        microphoneStopping={recording.recorder.stopping}
        recordingFailed={recording.offer === 'failed'}
        run={writes.run}
        onGeneratePrep={writes.generatePrep}
        onSavePrep={writes.savePrep}
        onSaveReport={writes.saveReport}
        onRegenerate={(which) => setDialog({ kind: 'regenerate', panel: which })}
      />

      <MeetingDialogs
        dialog={dialog}
        closeDialog={closeDialog}
        page={page}
        userId={user.id}
        meetingId={meetingId}
        attendees={attendees.all}
        correction={writes.correction}
        generation={writes.generation}
        sharingNotes={writes.pasteGenerate.isPending}
        run={writes.run}
        onAdvanceTemplate={advanceTemplate}
        onSaveTemplate={saveTemplate}
        onShareNotes={shareNotes}
      />

      <ManualMeetingDialogs
        dialog={dialog}
        closeDialog={closeDialog}
        page={page}
        userId={user.id}
        meetingId={meetingId}
        templates={withCurrentTemplate(templates, page.template)}
        onLeave={onLeave}
      />

      <MeetingRecordingDialogs recording={recording} />
    </div>
  );
}

/*
 * Wundo writes the preparation the first time its tab is on screen, and again
 * when it comes back to it. The server does nothing once it has written one, so
 * asking on a later visit is safe; where nothing can be written, it is not asked.
 */
function useFirstPrepGeneration(
  page: MeetingPageResponse | undefined,
  panel: MeetingPanel,
  generate: () => void,
) {
  const asked = useRef(false);
  const wanted = panel === 'prep' && page !== undefined;
  const missing = wanted && !page.prep?.content && page.prep_available;
  useEffect(() => {
    if (!wanted) {
      asked.current = false;
      return;
    }
    if (asked.current || !missing) return;
    asked.current = true;
    generate();
  }, [wanted, missing, generate]);
}

/* The one dialog the page has open. A write closes only the dialogs it answers. */
function useMeetingDialog() {
  const [dialog, setDialog] = useState<MeetingDialog | null>(null);
  const closeDialog = useCallback((kinds: MeetingDialogKind[]) => {
    setDialog((current) => (current && kinds.includes(current.kind) ? null : current));
  }, []);
  return { dialog, setDialog, closeDialog };
}

/*
 * A picked gesprekstype asks how far it reaches where the conversation has
 * more meetings, then whether to write the preparation again where that is
 * still possible. With neither to ask, the pick saves.
 */
function nextTemplateStep(
  page: MeetingPageResponse,
  pick: MeetingTemplatePick,
): TemplateStep {
  if (pick.scope === null && (page.thread?.meeting_count ?? 0) > 1) {
    return { kind: 'ask', pick };
  }
  const scope = pick.scope ?? 'occurrence';
  if (page.prep_available) return { kind: 'ask', pick: { ...pick, scope } };
  return {
    kind: 'save',
    choice: { templateId: pick.template.id, recreate: false, scope },
  };
}

/*
 * The gesprekstypes, fetched once they are wanted: when the type menu is first
 * opened, or when the edit dialog asks the same question.
 */
function useMeetingTemplates(userId: string | undefined, wanted: boolean) {
  const { data: templates = [] } = useQuery({
    queryKey: ['meetingTemplates', userId, 'use'],
    queryFn: () => fetchMeetingTemplates(userId ?? '', 'use'),
    enabled: Boolean(userId) && wanted,
  });
  return templates;
}

/*
 * The editor's copy and upload path, shared by both documents. A file dropped
 * into a document joins the conversation, so the page reads itself again.
 * Keyed on the ids: a refetched profile or page is no reason to rewire.
 */
function useEditorWiring(
  userId: string,
  page: MeetingPageResponse | undefined,
  onDocumentAdded: () => void,
) {
  const threadId = page?.thread?.id ?? null;
  return useMemo(
    () =>
      userId ? buildRichTextEditorWiring({ userId, threadId, onDocumentAdded }) : null,
    [userId, threadId, onDocumentAdded],
  );
}

/*
 * The microphone offered on the page itself, where there is no online link and
 * so no notetaker. Gone once there is something to show for it: a transcript,
 * or the verslag written from it.
 */
function offersRecordingHere(
  page: MeetingPageResponse,
  recordingState: OfflineRecordingState,
): boolean {
  return (
    page.decision.follow &&
    !page.meeting.join_url &&
    recordingState === 'idle' &&
    !page.report &&
    !page.materials.some((material) => material.kind === 'transcript')
  );
}
